import type { AuthUser } from '@/api/models';
import type { AuthMethod } from '@/utils/authSession';

/**
 * Who may see the salaries register.
 *
 * **Every route under `api/v1/ledger/salaries` is `OrgAdmin`, reads included.**
 * That is a deliberate departure from the fixed-asset register this screen is
 * modelled on, which opens its reads to `BranchAdmin`, and the reason is what the
 * reads contain: they are org-wide and not branch-scoped, so the register returns
 * every colleague's gross salary, personal number and pension note, and a run
 * returns each one's net pay. A BranchAdmin is a store manager. That is a payroll
 * officer's read, not a shop-floor one.
 *
 * The policy also requires `auth_method=password` — the same constraint
 * `settingsAccess` documents. A BranchAdmin holding a PIN-issued kiosk token is a
 * BranchAdmin, and still 403s; so, here, does an OrgAdmin on a kiosk token.
 *
 * | | policy | this client |
 * | --- | --- | --- |
 * | everything | `OrgAdmin` = OrgAdmin + password | `full` |
 *
 * Deriving it here — rather than letting the page discover it from a 403 — is
 * what lets the screen explain *why* it is unavailable, and what keeps a
 * BranchAdmin session from rendering a screen whose every request can only fail.
 * A raw 403 tells the user nothing they can act on.
 */
export type SalariesAccess =
    /**
     * A PIN/kiosk session. Every salaries endpoint will 403 whatever the role,
     * so the screen must not fetch at all — it explains that payroll needs a
     * password sign-in.
     */
    | { kind: 'kiosk' }
    /**
     * Signed in with a password, but not an OrgAdmin — a BranchAdmin or a
     * Seller. Payroll is not readable at this role, and saying so is the whole
     * point: there is nothing here to degrade to, because there is no
     * branch-scoped view of the register to offer.
     */
    | { kind: 'forbidden' }
    /** OrgAdmin with a password session: the register, its runs and its writes. */
    | { kind: 'full' };

/**
 * Derive the access level from the persisted session.
 *
 * Fails closed on every unknown: no user, or an `auth_method` that could not be
 * read, is treated as a kiosk session rather than optimistically rendering a
 * screen whose every request would 403.
 */
export const deriveSalariesAccess = (
    user: AuthUser | null,
    authMethod: AuthMethod | null,
): SalariesAccess => {
    if (!user) {
        return { kind: 'kiosk' };
    }
    // Unknown is not password. Rendering the register and letting each request
    // 403 is strictly worse than saying why it is unavailable.
    if (authMethod !== 'password') {
        return { kind: 'kiosk' };
    }
    return user.role === 'OrgAdmin' ? { kind: 'full' } : { kind: 'forbidden' };
};

/**
 * Whether this access level may read the register at all — which is also
 * whether any query on this screen may run.
 */
export const canReadSalaries = (access: SalariesAccess): boolean =>
    access.kind === 'full';

/** The i18n key explaining why the screen is unavailable at this level. */
export const SALARIES_ACCESS_COPY: Record<
    Exclude<SalariesAccess['kind'], 'full'>,
    { titleKey: string; bodyKey: string }
> = {
    kiosk: {
        titleKey: 'salaries.access.kiosk.title',
        bodyKey: 'salaries.access.kiosk.body',
    },
    forbidden: {
        titleKey: 'salaries.access.forbidden.title',
        bodyKey: 'salaries.access.forbidden.body',
    },
};
